import { Loading, useFetch } from "@4uhub/lib4uhub";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { tokenTemporaryService } from "../../../../services/tokenTemorary.service";
import NoData from "../../../UI/NoData";
import { IExamsResultLabPdfsModalProps } from "./models";
import { ILaboratorialExamsResultLabPdfResponse } from "../models";
import { FileItem } from "../../ReportsPdfSerial/ReportsPdfSerialsModal/FileItem/FileItem";
import {
  getLaboratorialExamsResultLabPdfList,
  getLaboratorialExamsResultLabPdfListAsTemporary,
} from "../../../../services/laboratorialExams.service";

export const ExamsResultLabPdfsModal = memo(
  ({ identifier, cdnUrl }: IExamsResultLabPdfsModalProps) => {
    const theme = useTheme();
    const matches = useMediaQuery(theme.breakpoints.down("md"));

    const [origins, setOrigins] = useState<
      ILaboratorialExamsResultLabPdfResponse[]
    >([]);
    const [selectedFile, setSelectedFile] = useState<
      string | ArrayBuffer | null
    >(null);
    const [selectedFileIndex, setSelectedFileIndex] = useState<number>(0);
    const [shouldAutoOpen, setShouldAutoOpen] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_, setFileLoading] = useState(false);

    const isTemporaryAccess = !!tokenTemporaryService.getTemporaryAccessToken();

    const { sendRequest, loading } = useFetch(
      isTemporaryAccess
        ? getLaboratorialExamsResultLabPdfListAsTemporary
        : getLaboratorialExamsResultLabPdfList,
    );

    const fetchOrigin = useCallback(async () => {
      const { data, success } = await sendRequest(identifier);
      if (data && success) {
        setOrigins(data);
        setSelectedFileIndex(0);
        if (data.length === 1) {
          setShouldAutoOpen(true);
        }
      }
    }, [identifier, sendRequest]);

    const selectFileHandler = useCallback(
      (value: string | ArrayBuffer | null) => {
        setSelectedFile(value);
      },
      [],
    );

    useEffect(() => {
      fetchOrigin();
    }, [fetchOrigin]);

    useEffect(() => {
      if (!shouldAutoOpen || !selectedFile) return;

      setShouldAutoOpen(false);

      try {
        let blob: Blob;

        if (selectedFile instanceof ArrayBuffer) {
          blob = new Blob([selectedFile], { type: "application/pdf" });
        } else {
          const base64String = (selectedFile as string).includes(",")
            ? (selectedFile as string).split(",")[1]
            : (selectedFile as string);

          const byteCharacters = atob(base64String);
          const byteArray = new Uint8Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i);
          }
          blob = new Blob([byteArray], { type: "application/pdf" });
        }

        const blobUrl = URL.createObjectURL(blob);
        const newTab = window.open();
        if (newTab) newTab.location.href = blobUrl;
      } catch (e) {
        console.error("[PDF] Erro ao abrir arquivo:", e);
      }
    }, [selectedFile, shouldAutoOpen]);

    if (loading) {
      return (
        <Box
          sx={{
            maxHeight: matches ? "calc(100vh - 220px)" : "calc(100vh - 260px)",
          }}
        >
          <Loading size={30} />
        </Box>
      );
    }

    if (origins.length === 0 && !loading) {
      return (
        <Box
          sx={{
            maxHeight: matches ? "calc(100vh - 220px)" : "calc(100vh - 260px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <NoData />
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 0.5,
          maxHeight: matches ? "calc(100vh - 220px)" : "calc(100vh - 260px)",
          overflowY: "auto",
        }}
      >
        {origins.map((origin, index) => (
          <FileItem
            cdnUrl={cdnUrl}
            key={origin.identifier + index}
            index={index}
            selectedFile={selectedFile}
            identifier={origin.identifier}
            isActive={selectedFileIndex === index}
            onSelectFile={selectFileHandler}
            onFileLoading={setFileLoading}
            onGetFileIndex={setSelectedFileIndex}
          />
        ))}
      </Box>
    );
  },
);
